<template>
  <email-alert-notification
    v-model="show"
    :emailTemplate="emailTemplate"
    :defaulter="defaulter"
    :suggestedList="attendees"
    @send="onEmailSend"
    type="remind"
    :heading="alertHeading"
    @close="onClose"
    :section="isMeetingDeleted ? 'meetingDelete' : ''"
  ></email-alert-notification>
</template>

<script>
import EmailAlertNotification from "../EmailAlertNotification.vue";
import { Internationalization } from "@syncfusion/ej2-base";
import { mapGetters, mapState } from "vuex";
import PlaceholderMixin from "../../mixin/PlaceholderMixin";

export default {
  name: "MeetingEmailNotification",
  components: { EmailAlertNotification },
  mixins: [PlaceholderMixin],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    meetingObject: Object,
    isMeetingDeleted: {
      type: Boolean,
      default: false,
    },
    isMeetingCreated: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState({
      agendaListData: (state) => state.agenda.agendaListByMeetingType,
    }),
    ...mapGetters({
      getTemplateByTemplateTypeId: "tenant/getTemplateByTemplateTypeId",
      tenantSettingTypes: "tenant/tenantSettingTypes",
    }),
    topicDetails() {
      const topicCategoryList = this.tenantSettingTypes("topiccategory") || [];

      let maxOrder = topicCategoryList.reduce(
        (max, { order = 0 }) => Math.max(max, order),
        0
      );

      // Assign new order to items that already have an order
      const updatedList = topicCategoryList.map((cat) => {
        const updatedOrder = cat.order ? ++maxOrder : cat.order;
        return { ...cat, order: updatedOrder };
      });

      // Apply getTopicOrder and sort the list
      return updatedList
        .map((item) => ({
          ...item,
          order: this.getTopicOrder(item),
        }))
        .sort((a, b) => a.order - b.order);
    },
    topics() {
      const topics = this.meetingObject?.topics || [];

      return topics.map((item) => ({
        ...item,
        agenda: this.getAgendaDetails(item),
      }));
    },
    agendanumbering() {
      return this.tenantSettingTypes("agendanumbering");
    },
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value || false);
      },
    },
    getMeetingUpdateTemplate() {
      return this.getTemplateByTemplateTypeId(
        "MeetingUpdate",
        this.selectedLang
      );
    },
    getMeetingCreateTemplate() {
      return this.getTemplateByTemplateTypeId(
        "MeetingCreate",
        this.selectedLang
      );
    },
    getMeetingDeleteTemplate() {
      return this.getTemplateByTemplateTypeId(
        "MeetingDeletion",
        this.selectedLang
      );
    },
    tenantName() {
      const users = this.$auth.user[process.env.VUE_APP_TENANT_KEY];
      const tenant = users.find((u) => u.id === this.selectedTenant);
      if (tenant) {
        return tenant.name;
      }
      return "";
    },
    selectedLang() {
      return this.$i18n.locale;
    },
    displayDateFormat() {
      return this.selectedLang !== "ja"
        ? "dd/MM/yyyy hh:mm aaa"
        : "yyyy/MM/dd HH:mm";
    },
    dateFormat() {
      return this.selectedLang !== "ja" ? "dd/MM/yyyy" : "yyyy/MM/dd";
    },
    emailTemplate() {
      let meetingNumbering = 0;

      const catTopics = this.topicDetails
        .map((category) => ({
          ...category,
          topics: this.topics
            .filter((topic) => topic.topicCategory === category.id)
            .sort((a, b) => a.displayOrder - b.displayOrder),
        }))
        .filter((category) => category.topics.length > 0)
        .sort((a, b) => a.order - b.order);

      // Assign meetingNumbering
      catTopics.forEach((category) => {
        category.topics.forEach((topic) => {
          topic.meetingNumbering = ++meetingNumbering;
        });
      });

      let meetingPurpose = catTopics
        .map((category) => {
          const topicsHtml = category.topics
            .map((topic, index) => {
              const number =
                this.agendanumbering === "numbering_for_meeting"
                  ? `${topic.meetingNumbering}.`
                  : this.agendanumbering === "numbering_for_category"
                  ? `${index + 1}.`
                  : "";

              const liStyle =
                this.agendanumbering === "" ||
                this.agendanumbering === "no_numbering"
                  ? ""
                  : "list-style-type: none";

              return `
          <li>
            ${topic.agenda.agendaName}
            <ul>
              <li style="${liStyle}">${number} ${topic.topicName}</li>
            </ul>
          </li>`;
            })
            .join("");

          return `
      <ul style="list-style: none; margin-left: 0; padding-left: 0; font-size: 11pt;">
        <li>${category.name}
          <ul style="list-style: none;">
            ${topicsHtml}
          </ul>
        </li>
      </ul>`;
        })
        .join("");

      const placeHolderVariables = this.replaceMeetingDataPlaceholders(
        this.meetingObject,
        meetingPurpose
      );

      const meetingTemplate = this.isMeetingDeleted
        ? this.getMeetingDeleteTemplate
        : this.isMeetingCreated
        ? this.getMeetingCreateTemplate
        : this.getMeetingUpdateTemplate;

      return {
        subject: meetingTemplate.subject.replace(
          /{{%[^}]+%}}/g,
          (match) => placeHolderVariables[match] || ""
        ),
        message: meetingTemplate.body.replace(
          /{{%[^}]+%}}/g,
          (match) => placeHolderVariables[match] || ""
        ),
        mailType: this.mailType,
        dynamicVariables: {
          targetId: this.meetingObject.id,
          targetTitle: this.meetingObject.meetingName,
          meetingTypeId: this.meetingObject.meetingTypeId,
          targetLinkLabel: this.$t("agenda.meetingTargetLinkLabel"),
          targetLink: this.getTargetLink(),
        },
      };
    },
    mailType() {
      return this.isMeetingDeleted
        ? "meeting-delete-alert"
        : "meeting-voting-alert";
    },
    meetingType() {
      return this.meetingObject.meetingType.label;
    },
    attendees() {
      return this.meetingObject.attendees;
    },
    selectedTenant() {
      return sessionStorage.getItem("tenantId");
    },
    alertHeading() {
      return this.$t("agenda.hNotification");
    },
    tenantName() {
      const users = this.$auth.user[process.env.VUE_APP_TENANT_KEY];
      const tenant = users.find((u) => u.id === this.selectedTenant);
      if (tenant) {
        return tenant.name;
      }
      return "";
    },
    lblCreated() {
      return this.$t("email.lblCreated");
    },
    lblUpdated() {
      return this.$t("email.lblUpdated");
    },
    lblDeleted() {
      return this.$t("email.lblDeleted");
    },
  },
  data() {
    return {
      defaulter: [],
      intl: new Internationalization(),
    };
  },
  methods: {
    onEmailSend,
    getFormattedMeetingTime,
    getMailBodyTitle,
    getMeetingType,
    getTargetLink,
    onClose,
    getTopicOrder,
    getAgendaDetails,
  },
};

function getAgendaDetails(item) {
  return this.agendaListData?.find((f) => f.id === item.agendaId) || {};
}

function getTopicOrder(item) {
  const category = this.meetingObject.topicCategories?.find(
    (f) => f.id === item.id
  );
  return category?.order ?? item.order;
}

function getMailBodyTitle(meetingName) {
  const action = this.isMeetingDeleted
    ? this.lblDeleted
    : this.isMeetingCreated
    ? this.lblCreated
    : this.lblUpdated;

  return this.$t("email.emailBodyTitle", {
    meetingName,
    action,
  });
}

function getMeetingType(meeting) {
  return this.isMeetingDeleted
    ? meeting.meetingType.label
    : meeting.meetingType;
}

function getTargetLink() {
  return this.isMeetingDeleted
    ? null
    : `${window.location.origin}/meeting/${this.meetingObject.id}/agenda/tenant/${this.selectedTenant}`;
}

function onEmailSend(isSuccess) {
  this.$emit("send", isSuccess);
}

function onClose() {
  this.$emit("close");
}

function getFormattedMeetingTime(meetingDetails) {
  let meetingTime =
    this.intl.formatDate(new Date(meetingDetails.scheduletStartTimeInUTC), {
      type: "date",
      skeleton: "long",
    }) +
    " " +
    this.intl.formatDate(new Date(meetingDetails.scheduletStartTimeInUTC), {
      skeleton: "hm",
    }) +
    " - " +
    this.intl.formatDate(new Date(meetingDetails.scheduledEndTimeInUTC), {
      type: "date",
      skeleton: "long",
    }) +
    " " +
    this.intl.formatDate(new Date(meetingDetails.scheduledEndTimeInUTC), {
      skeleton: "hm",
    });
  return meetingTime;
}
</script>

<style>
</style>